import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import { useSearchDeployedNamespaces } from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import { useMemo } from 'react';
import type { InterfaceModes } from '../../../../../stores/InterfaceStore';
import InterfaceHelper from '../../../../../stores/InterfaceStore/helper/InterfaceHelper';
import { useGetUserContext } from '../../../../useGetUserContext';
import {
  bucketNamespacesByComponentId,
  collectPageAndInterfaceIds,
  getShouldFetchNamespace,
} from '../../../standardEntities/data/useFetchInterfaceNamespaces/utils';
import { getDeployedInterfaceComponentNamespacesByIdsSearchRequest } from './utils';

type Args = {
  interfaceId: string;
  pages: (InterfacePageEntity | undefined)[];
  interfaceRecord: InterfaceEntity | undefined;
  mode?: InterfaceModes;
};

/**
 * Multi-page deployed-namespace fetcher. Mirrors the standard variant - one
 * IN-filtered searchDeployedEntities call resolves all page namespaces in
 * 1 request.
 */
export function useFetchDeployedInterfacePageNamespacesForPages({
  interfaceId,
  pages,
  interfaceRecord,
  mode,
}: Args) {
  const { userContext } = useGetUserContext();

  const locale = InterfaceHelper.getLocale({
    userLocale: userContext?.locale,
    interfaceRecord,
    mode,
  });
  const shouldFetchNamespace = getShouldFetchNamespace(locale, interfaceRecord);

  const { pageIds, interfaceIds } = useMemo(
    () => collectPageAndInterfaceIds(pages, interfaceId),
    [pages, interfaceId],
  );

  const {
    data: namespaces,
    isLoading,
    error,
  } = useSearchDeployedNamespaces(
    getDeployedInterfaceComponentNamespacesByIdsSearchRequest({
      interfaceIds,
      interfaceComponentIds: pageIds,
      locale,
    }),
    {
      query: {
        select: (resp) => bucketNamespacesByComponentId(resp),
        enabled: pageIds.length > 0 && interfaceIds.length > 0 && shouldFetchNamespace,
        retry: 3,
        staleTime: Infinity,
      },
    },
  );

  return { namespaces, isLoading, error };
}
